import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import contractPendingSignatureService from '../services/contractPendingSignatureService';

const ContractSignPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contractData, setContractData] = useState<any>(null);
  const [accepted, setAccepted] = useState(false);
  const [showSignaturePanel, setShowSignaturePanel] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (token) {
      loadContractData();
    }
  }, [token]);

  const loadContractData = async () => {
    try {
      setLoading(true);
      const data = await contractPendingSignatureService.getSignPageData(token!);
      setContractData(data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Błąd ładowania danych umowy');
    } finally {
      setLoading(false);
    }
  };

  // Inicjalizacja canvas do podpisu
  useEffect(() => {
    if (showSignaturePanel && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Ustaw rozmiar canvas
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      
      // Style
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [showSignaturePanel]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setHasSignature(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureData(null);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataURL = canvas.toDataURL('image/png');
    setSignatureData(dataURL);
  };

  const handleNext = () => {
    if (!accepted) {
      alert('Musisz zaakceptować warunki');
      return;
    }
    setShowSignaturePanel(true);
  };

  const handleSubmit = async () => {
    if (!signatureData) {
      alert('Musisz złożyć podpis');
      return;
    }

    try {
      setSubmitting(true);
      await contractPendingSignatureService.submitSignature(token!, signatureData, accepted);
      alert('Podpis został zapisany pomyślnie!');
      // Można przekierować na stronę potwierdzenia
      navigate('/');
    } catch (error: any) {
      alert('Błąd zapisywania podpisu: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!contractData) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA', p: { xs: 2, md: 4 } }}>
      <Paper sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontFamily: 'Roboto, sans-serif', fontWeight: 500 }}>
          Umowa do podpisu
        </Typography>

        {!showSignaturePanel ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Roboto, sans-serif' }}>
                <strong>Numer umowy:</strong> {contractData.contract.contract_number}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Roboto, sans-serif' }}>
                <strong>Data umowy:</strong> {contractData.contract.contract_date}
              </Typography>
            </Box>

            <Box 
              sx={{ 
                border: '1px solid #d4d4d9', 
                borderRadius: 1, 
                p: 2, 
                mb: 3,
                maxHeight: '60vh',
                overflowY: 'auto',
                bgcolor: '#fff'
              }}
              dangerouslySetInnerHTML={{ __html: contractData.contract.gdoc_content }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
              }
              label="Przeczytałem i akceptuję warunki umowy"
              sx={{ mb: 3, fontFamily: 'Roboto, sans-serif' }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
              >
                Anuluj
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!accepted}
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  textTransform: 'none',
                  backgroundColor: '#020242',
                  '&:hover': { backgroundColor: '#030360' }
                }}
              >
                Dalej
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Roboto, sans-serif' }}>
              Podpisz umowę
            </Typography>

            <Box sx={{ mb: 2 }}>
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                style={{
                  border: '2px solid #d4d4d9',
                  borderRadius: 4,
                  width: '100%',
                  height: '300px',
                  cursor: 'crosshair',
                  touchAction: 'none'
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                onClick={clearSignature}
                sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
              >
                Wyczyść
              </Button>
              <Button
                variant="outlined"
                onClick={saveSignature}
                disabled={!hasSignature}
                sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
              >
                Zapisz podpis
              </Button>
            </Box>

            {signatureData && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Roboto, sans-serif' }}>
                  Podpis zapisany
                </Typography>
                <img src={signatureData} alt="Podpis" style={{ maxWidth: '100%', border: '1px solid #d4d4d9', borderRadius: 4 }} />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setShowSignaturePanel(false)}
                sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
              >
                Wstecz
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!signatureData || submitting}
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  textTransform: 'none',
                  backgroundColor: '#020242',
                  '&:hover': { backgroundColor: '#030360' }
                }}
              >
                {submitting ? <CircularProgress size={24} /> : 'Podpisz'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ContractSignPage;

